/**
 * Voxi widget: ElevenLabs conversation (voice or text) + concierge SSE stream + rich cards.
 * All state changes go through the agent/concierge; the widget never talks to Vista directly.
 */
import { type DisconnectionDetails, useConversation } from "@elevenlabs/react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { type BookingHistory, type CustomerProfile, type Customer, type Lang, type Session, type UiHint, type WidgetEvent, createSession, devTool, getCustomerProfile, getSignedUrl, getState, isAuthorizationError, linkConversation, sendCommand, sessionExpiryHandler, subscribe, widgetLogin, widgetLogout } from "../lib/api";
import { stripDeliveryTags } from "../lib/delivery-tags";
import { isRtl, t } from "../lib/i18n";
import { type CardActions, Cards, createTicketQr, Feedback, seatRange } from "./Cards";
import { BrandLogo } from "./BrandLogo";
import { BrandMark, Icon } from "./V3";
import { type Loc, LocationBar } from "./LocationBar";
import { ACCOUNT_ACTIVITY_EVENT, AUTH_CHANGE_SIGNAL, notifyPageAuthChange, pageSession, usePageSession, type PageSessionRuntime } from "../lib/page-session";
import { acceptWidgetEvent, actionContext, appendTranscript, directSeatMapFeedback, holdSeconds, isCurrentHold, recordUserActivity, renderVerifiedSeatMap, verifyHoldNotice, type HoldNoticeSnapshot, type TranscriptBody as ItemBody, type TranscriptItem as Item } from "../lib/widget-state";
import widgetAcknowledgement from "../lib/widget-acknowledgement.json";
import { receiptCompletesCurrentOrder, renderVerifiedReceipt } from "../lib/receipt";
import { connectionVariables, isCurrentConnection, nextWelcomeVariant, type ConnectionSnapshot } from "../lib/welcome";
import { applyVerifiedHumanMode, type HumanMode } from "../lib/human-mode";
import { cancelledCurrentOrder, isCancelledOrderUi, removeCancelledOrderCards } from "../lib/order-cancellation";


const nid = () => crypto.randomUUID();
/** How long agent-triggered cards wait for the agent's text before showing anyway (the reply after a tool call can take ~10 s). */
// Multi-tool turns (hold, snacks, seat changes) now take 15–20 s of sequential LLM calls; the grace must outlast
// them or the cards land above the reply that explains them. Cards still flush on the next user message,
// on a human/transfer message, and when the connection ends.
const CARD_REPLY_GRACE_MS = 60000;

/** Earlier step: the card stays visible as a record, but nothing inside it can be pressed. */
function FrozenCards({ label, children }: { label: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { const el = ref.current; if (el) (el as HTMLDivElement & { inert: boolean }).inert = true; }, []);
  return <div ref={ref} className="cards-frozen" aria-disabled="true"><span className="frozen-tag" aria-hidden="true">{label}</span>{children}</div>;
}

/** How long a saved widget session may be resumed on this device: 30 minutes for a guest, 6 hours for a member. */
export function sessionResumeMs(saved: { loggedIn?: boolean }) {
  return saved.loggedIn ? 6 * 60 * 60000 : 30 * 60000;
}

export function Concierge({ initialLang = "en", initialOpen = true, onExpand, onAuth, onLanguage }: { initialLang?: Lang; initialOpen?: boolean; onExpand?: (b: boolean) => void; onAuth?: (c: Customer | null) => void; onLanguage?: (language: Lang) => void }) {
  const [lang, setLang] = useState<Lang>(initialLang);
  const [session, setSession] = useState<Session | null>(null);
  const langRef = useRef(lang);
  langRef.current = lang;
  const sessionRef = useRef<Session | null>(null);
  const authEpochRef = useRef(0);
  const authBusyRef = useRef(false);
  const restoreRef = useRef<Promise<void> | null>(null);
  const pendingLinkRef = useRef<Promise<void> | null>(null);
  const endingSessionRef = useRef<Promise<void> | null>(null);
  const expiredConnectionRef = useRef(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const connectionAttemptRef = useRef(0);
  const startingRef = useRef(false);
  const commitSession = useCallback((next: Session | null) => {
    sessionRef.current = next;
    setSession(next);
  }, []);
  // The page owns account presentation; this runtime preserves the existing voice/link ordering.
  const [customer, setCustomer] = useState<Customer | null>(null);
  const { open: authOpen } = usePageSession();
  const setAuthOpen = (value: boolean) => value ? pageSession.requestAccount() : pageSession.close();
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState<string | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [history, setHistory] = useState<BookingHistory[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const refreshProfile = async (current: Session) => {
    if (!current.isLoggedIn) return;
    const epoch = authEpochRef.current;
    const stillCurrent = () => epoch === authEpochRef.current && sessionRef.current?.token === current.token && sessionRef.current.conversationId === current.conversationId;
    setProfileLoading(true);
    try {
      const details = await getCustomerProfile(current);
      if (!stillCurrent()) return;
      setCustomer(details.customer); setProfile(details.profile); setHistory(details.history);
      onAuth?.(details.customer);
    } catch { if (stillCurrent()) setAuthErr(langRef.current === "ar" ? "تعذر تحميل حسابك. حاول مرة أخرى." : "Your account could not load. Try again."); }
    finally { if (stillCurrent()) setProfileLoading(false); }
  };
  useEffect(() => {
    const openIt = () => {
      setAuthOpen(true);
    };
    const logoutIt = () => void doLogout();
    const accountActivity = () => handleUserActivity();
    const openOnly = (event: Event) => {
      setOpen(true);
      const message = (event as CustomEvent<{ message?: unknown }>).detail?.message;
      if (typeof message === "string" && message.trim()) setInput(message.trim().slice(0, 500));
    };
    window.addEventListener("voxi:login", openIt);
    window.addEventListener("voxi:profile", openIt);
    window.addEventListener("voxi:logout", logoutIt);
    window.addEventListener("voxi:open", openOnly);
    window.addEventListener(ACCOUNT_ACTIVITY_EVENT, accountActivity);
    return () => {
      window.removeEventListener("voxi:login", openIt);
      window.removeEventListener("voxi:profile", openIt);
      window.removeEventListener("voxi:logout", logoutIt);
      window.removeEventListener("voxi:open", openOnly);
      window.removeEventListener(ACCOUNT_ACTIVITY_EVENT, accountActivity);
    };
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const doLogin = async (email: string, password: string) => {
    if (authBusyRef.current) return;
    authBusyRef.current = true;
    const epoch = ++authEpochRef.current;
    setAuthBusy(true);
    setAuthErr(null);
    try {
      await restoreRef.current;
      await pendingLinkRef.current;
      await endingSessionRef.current;
      if (epoch !== authEpochRef.current) return;
      const s = sessionRef.current ?? (await createSession({ language: langRef.current, modality: "text" }));
      if (epoch !== authEpochRef.current) return;
      if (!sessionRef.current) commitSession(s);
      const r = await widgetLogin(s, email, password);
      if (epoch !== authEpochRef.current) return;
      if (!r.ok || !r.customer) {
        setAuthErr(r.error ?? (lang === "ar" ? "تعذّر تسجيل الدخول" : "Could not sign in"));
        return;
      }
      const next = { ...s, token: r.token ?? s.token, isLoggedIn: true, dynamicVariables: r.dynamicVariables ?? s.dynamicVariables };
      if (customer?.id !== r.customer.id) {
        setProfile(null); setHistory([]); setProfileLoading(true);
        pageSession.update({ customer: r.customer, profile: null, history: [], loading: true });
      }
      commitSession(next);
      setSessionExpired(false);
      const previousCustomerId = s.isLoggedIn ? s.dynamicVariables.customerId || customer?.id : undefined;
      if (previousCustomerId && previousCustomerId !== r.customer.id) {
        ++connectionAttemptRef.current; startingRef.current = false; switchingRef.current = true;
        try { await conversation.endSession(); } catch { /* Server identity already changed. */ } finally { switchingRef.current = false; }
        if (epoch !== authEpochRef.current) return;
        setItems([]); commitOrder(null); setMode("idle"); clearHumanMode();
        pendingAckRef.current.clear(); completedActionsRef.current.clear(); cancelledOrdersRef.current.clear(); ackQueueRef.current = [];
      }
      setCustomer(r.customer);
      void refreshProfile(next);
      onAuth?.(r.customer);
      setAuthOpen(false);
      notifyPageAuthChange();
      push({ kind: "note", text: lang === "ar" ? "تم تسجيل الدخول" : "Signed in" });
      if (statusRef.current === "connected")
        conversation.sendContextualUpdate(
          "The page sign-in succeeded. Refresh get_session_context before using account or booking information. Continue the guest's existing request without repeating questions or reading their profile aloud.",
        );
    } catch {
      setAuthErr(lang === "ar" ? "تعذر تسجيل الدخول. حاول مرة أخرى." : "Could not sign in. Please try again.");
    } finally {
      authBusyRef.current = false;
      setAuthBusy(false);
    }
  };
  const doLogout = async () => {
    if (authBusyRef.current) return;
    authBusyRef.current = true;
    ++authEpochRef.current;
    setAuthBusy(true); setAuthErr(null);
    try {
      await restoreRef.current;
      await pendingLinkRef.current;
      const current = sessionRef.current;
      if (!current) return;
      const result = await widgetLogout(current);
      if (!result.ok) throw new Error("Sign-out failed");
      ++connectionAttemptRef.current;
      startingRef.current = false;
      switchingRef.current = true;
      try { await conversation.endSession(); } catch { /* Backend sign-out already succeeded. */ } finally { switchingRef.current = false; }
      commitSession({ ...current, token: result.token ?? current.token, isLoggedIn: false, dynamicVariables: result.dynamicVariables ?? current.dynamicVariables });
      setCustomer(null); setProfile(null); setHistory([]); setProfileLoading(false); commitOrder(null); setItems([]); setAuthOpen(false); setMode("idle"); clearHumanMode();
      pendingAckRef.current.clear(); completedActionsRef.current.clear(); cancelledOrdersRef.current.clear(); ackQueueRef.current = [];
      onAuth?.(null);
      notifyPageAuthChange();
    } catch { setAuthErr(lang === "ar" ? "تعذر تسجيل الخروج. حاول مرة أخرى." : "Could not sign out. Please try again."); }
    finally { authBusyRef.current = false; setAuthBusy(false); }
  };
  const [items, setItems] = useState<Item[]>([]);
  const itemsRef = useRef<Item[]>([]);
  itemsRef.current = items;
  const [mode, setModeState] = useState<"idle" | "voice" | "text">("idle");
  // Live transport mode for callbacks that may run before React re-renders (e.g. the first typed message,
  // which starts the text session and then sends itself in the same call).
  const modeRef = useRef<"idle" | "voice" | "text">("idle");
  const setMode = useCallback((m: "idle" | "voice" | "text") => { modeRef.current = m; setModeState(m); }, []);
  // ---- booking v2: the live order (seat hold timer), mute, inactivity ----
  type LiveOrder = { userSessionId: string; expiresAtUtc?: string; totalCents?: number; filmTitle?: string; seats?: string; paid?: boolean };
  const [order, setOrder] = useState<LiveOrder | null>(null);
  const orderRef = useRef<LiveOrder | null>(null);
  orderRef.current = order;
  const commitOrder = (next: LiveOrder | null | ((previous: LiveOrder | null) => LiveOrder | null)) => {
    const value = typeof next === "function" ? next(orderRef.current) : next;
    orderRef.current = value;
    setOrder(value);
  };
  const accountRuntime = useRef<PageSessionRuntime>({ login: doLogin, logout: doLogout });
  accountRuntime.current = { login: doLogin, logout: doLogout };
  useEffect(() => pageSession.attach({ login: (email, password) => accountRuntime.current.login(email, password), logout: () => accountRuntime.current.logout() }), []);
  useEffect(() => {
    pageSession.update({ customer, profile, history, loading: profileLoading, busy: authBusy, error: authErr, language: lang });
  }, [customer, profile, history, profileLoading, authBusy, authErr, lang]);
  const [muted, setMuted] = useState(false);
  const [holdLeft, setHoldLeft] = useState<number | null>(null); // seconds
  const warnedRef = useRef<{ two?: string; short?: string; expired?: string }>({});
  const holdCheckRef = useRef<{ key: string; pending: boolean; retryAt: number } | null>(null);
  const activityRef = useRef({ lastUserAt: Date.now(), lastProviderPingAt: Number.NEGATIVE_INFINITY });
  const idleEndedRef = useRef(false);
  const switchingRef = useRef(false);
  const eventSeqRef = useRef(new Map<string, number>());
  const eventIdsRef = useRef(new Set<string>());
  const pendingAckRef = useRef(new Set<string>());
  const completedActionsRef = useRef(new Map<string, { type: string; status: string; result?: Record<string, unknown>; error?: { message: string } }>());
  const cancelledOrdersRef = useRef(new Set<string>());
  const ackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ackQueueRef = useRef<string[]>([]);
  const [humanMode, storeHumanMode] = useState<HumanMode | null>(null);
  const humanModeRef = useRef<HumanMode | null>(null);
  const transferRevisionRef = useRef(0);
  const setHumanMode = useCallback((next: HumanMode | null | ((current: HumanMode | null) => HumanMode | null)) => {
    const value = typeof next === "function" ? next(humanModeRef.current) : next;
    transferRevisionRef.current += 1;
    humanModeRef.current = value;
    storeHumanMode(value);
  }, []);
  const endedTransfersRef = useRef(new Set<string>());
  const clearHumanMode = () => {
    if (humanModeRef.current) endedTransfersRef.current.add(humanModeRef.current.transferId);
    setHumanMode(null);
  };
  const [endingChat, setEndingChat] = useState(false);
  const [input, setInput] = useState("");
  const [level, setLevel] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [sseStatus, setSseStatus] = useState<"open" | "closed">("closed");
  const [loc, setLoc] = useState<Loc | null>(() => {
    try {
      // A shared location lasts for this visit only; a later visit starts fresh (no remembered preferences).
      localStorage.removeItem("voxi.loc");
      const raw = sessionStorage.getItem("voxi.loc");
      return raw ? (JSON.parse(raw) as Loc) : null;
    } catch {
      return null;
    }
  });
  const locRef = useRef<Loc | null>(loc);
  locRef.current = loc;
  const changeLoc = (l: Loc | null) => {
    setLoc(l);
    try {
      if (l) sessionStorage.setItem("voxi.loc", JSON.stringify(l));
      else sessionStorage.removeItem("voxi.loc");
    } catch {}
    const s = sessionRef.current;
    if (s) void sendCommand(s, l ? { type: "location", lat: l.lat, lng: l.lng, accuracyM: l.accuracyM, label: l.label, source: l.source } : { type: "location.clear" });
  };
  const dev = new URLSearchParams(window.location.search).get("dev") === "1";
  const [devName, setDevName] = useState("search_films");
  const [devInput, setDevInput] = useState('{"query":"spider"}');
  const bodyRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<string>("disconnected"); // live connection status for callbacks that outlive a render
  const pushNow = useCallback((it: ItemBody) => {
    if (it.kind === "cards" && it.ui.type === "login") { pageSession.requestLogin(); return; }
    if (it.kind === "cards" && isCancelledOrderUi(it.ui, cancelledOrdersRef.current)) return;
    setItems((xs) => appendTranscript(xs, { ...it, id: nid() } as Item));
  }, []);
  // Cards produced by the agent's tool calls wait for the agent's reply, so the text always comes first.
  // They still show after a short grace period, on the next user message, or when the connection ends.
  const pendingCardsRef = useRef<{ items: ItemBody[]; timer: ReturnType<typeof setTimeout> | null }>({ items: [], timer: null });
  const flushPendingCards = useCallback(() => {
    const pending = pendingCardsRef.current;
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = null;
    for (const it of pending.items.splice(0)) pushNow(it);
  }, [pushNow]);
  const push = useCallback((it: ItemBody, options?: { afterReply?: boolean }) => {
    if (!options?.afterReply || it.kind !== "cards" || statusRef.current !== "connected") { pushNow(it); return; }
    const pending = pendingCardsRef.current;
    pending.items.push(it);
    if (!pending.timer) pending.timer = setTimeout(flushPendingCards, CARD_REPLY_GRACE_MS);
  }, [pushNow, flushPendingCards]);
  useEffect(() => () => { if (pendingCardsRef.current.timer) clearTimeout(pendingCardsRef.current.timer); }, []);
  const acknowledge = (context: string) => {
    ackQueueRef.current.push(context);
    if (ackTimerRef.current) clearTimeout(ackTimerRef.current);
    ackTimerRef.current = setTimeout(() => {
      const changes = ackQueueRef.current.splice(0);
      if (statusRef.current !== "connected" || !changes.length) return;
      conversation.sendUserMessage(widgetAcknowledgement.prefix + changes.join('; ') + '. ' + widgetAcknowledgement.instruction);
    }, 650);
  };
  useEffect(() => () => { if (ackTimerRef.current) clearTimeout(ackTimerRef.current); }, []);

  const changeLanguage = async (next: Lang, tellAgent = false) => {
    langRef.current = next;
    setLang(next);
    const current = sessionRef.current;
    if (current) {
      const result = await sendCommand(current, { type: "language", language: next }).catch(() => null);
      if (result?.ok && sessionRef.current?.token === current.token)
        commitSession({ ...sessionRef.current, language: next, dynamicVariables: { ...sessionRef.current.dynamicVariables, language: next } });
    }
    if (tellAgent && statusRef.current === "connected") conversation.sendUserMessage(next === "ar" ? "من فضلك تابع بالعربية." : "Please continue in English.");
  };
  useEffect(() => { if (initialLang !== langRef.current) void changeLanguage(initialLang, true); }, [initialLang]);
  useEffect(() => { onLanguage?.(lang); }, [lang, onLanguage]);

  // ---------- client tools (called by the agent) ----------
  const clientTools = useMemo(
    () => ({
      render_cards: async (p: { ui: UiHint }) => {
        return "The widget renders validated server tool results automatically. No additional cards are needed.";
      },
      render_seat_map: async (p: { sessionKey: string; userSessionId?: string }) => {
        const s = sessionRef.current;
        if (!s) return JSON.stringify({ ok: false, rendered: false, error: "The widget is not ready." });
        return JSON.stringify(await renderVerifiedSeatMap(
          () => sendCommand(s, { type: "seat.plan", sessionKey: p.sessionKey, userSessionId: p.userSessionId }),
          (ui) => { if (isCancelledOrderUi(ui, cancelledOrdersRef.current)) throw new Error("Order cancelled"); push({ kind: "cards", ui }, { afterReply: true }); },
          () => sessionRef.current?.token === s.token,
        ));
      },
      render_order_summary: async () => "The order summary is shown after each order step.",
      render_payment_sheet: async () => "The payment sheet appears after prepare_payment.",
      render_qr: async (p: { bookingId?: unknown }) => {
        const s = sessionRef.current;
        const epoch = authEpochRef.current;
        if (!s) return JSON.stringify({ ok: false, rendered: false, error: "The widget is not ready. Sign in to view your receipt." });
        return JSON.stringify(await renderVerifiedReceipt(
          p?.bookingId,
          (bookingId) => sendCommand(s, { type: "booking.receipt", bookingId }),
          createTicketQr,
          (ui) => { flushSync(() => push({ kind: "cards", ui }, { afterReply: true })); },
          () => sessionRef.current?.token === s.token && authEpochRef.current === epoch && !authBusyRef.current,
        ));
      },
      render_feedback: async () => {
        push({ kind: "feedback" });
        return "feedback shown";
      },
      request_location: async () => {
        const known = locRef.current;
        if (known) return JSON.stringify({ lat: known.lat, lng: known.lng, label: known.label, source: known.source });
        try {
          const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000 }));
          const l: Loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracyM: pos.coords.accuracy, label: langRef.current === "ar" ? "موقعي الحالي" : "My current location", source: "gps" };
          changeLoc(l);
          return JSON.stringify({ lat: l.lat, lng: l.lng, label: l.label, source: "gps" });
        } catch {
          return JSON.stringify({ error: "location_denied", hint: "ask the guest to tap the location button in the chat or name an area" });
        }
      },
      play_trailer: async (p: { youtubeId: string; title?: string }) => {
        window.open(`https://www.youtube.com/watch?v=${p.youtubeId}`, "_blank", "noopener");
        return "opened";
      },
      open_link: async (p: { url: string }) => {
        window.open(p.url, "_blank", "noopener");
        return "opened";
      },
      set_language: async (p: { language: Lang }) => {
        await changeLanguage(p.language);
        return "ok";
      },
      set_mode: async (p: { mode: "bot" | "human"; transferId?: string }) => {
        const s = sessionRef.current;
        if (!s) return JSON.stringify({ ok: false, error: "The current account session is unavailable." });
        const epoch = authEpochRef.current;
        const revision = transferRevisionRef.current;
        return JSON.stringify(await applyVerifiedHumanMode(p, s.conversationId, () => getState(s), setHumanMode,
          () => !authBusyRef.current && epoch === authEpochRef.current && revision === transferRevisionRef.current
            && sessionRef.current?.token === s.token && sessionRef.current.conversationId === s.conversationId,
          (id) => endedTransfersRef.current.has(id)));
      },
      confirm_dialog: async (p: { title: string; body: string; confirmLabel?: string; cancelLabel?: string }) => {
        const ok = window.confirm(`${p.title}\n\n${p.body}`);
        return ok ? "confirmed" : "cancelled";
      },
    }),
    [push],
  );

  const conversation = useConversation({
    clientTools,
    micMuted: muted,
    onConnect: () => { if (!expiredConnectionRef.current) push({ kind: "note", text: langRef.current === "ar" ? "متصل" : "Connected" }); },
    onDisconnect: (d?: DisconnectionDetails) => {
      flushPendingCards();
      if (switchingRef.current || expiredConnectionRef.current) return;
      setMode("idle");
      const why = d?.reason === "error" ? d.message : d?.reason === "agent" && d.context?.code && d.context.code !== 1000 ? `${d.context.code} ${d.context.reason ?? ""}`.trim() : "";
      if (idleEndedRef.current) { push({ kind: "note", text: langRef.current === "ar" ? "توقفت المحادثة بعد ثلاث دقائق من عدم النشاط. يمكنك المتابعة متى أردت." : "The conversation paused after three minutes of inactivity. Reconnect whenever you’re ready." }); return; }
      if (why) push({ kind: "note", text: `⚠️ ${langRef.current === "ar" ? "انقطع الاتصال" : "Connection closed"}: ${why}` });
      else push({ kind: "note", text: langRef.current === "ar" ? "انتهت المحادثة" : "Conversation ended" });
      if (!switchingRef.current && !idleEndedRef.current) push({ kind: "feedback" });
    },
    onError: (m: unknown, ctx?: unknown) => {
      if (expiredConnectionRef.current) return;
      const detail = typeof m === "string" ? m : (m as { message?: string })?.message ?? (ctx as { reason?: string })?.reason ?? "";
      push({ kind: "note", text: `⚠️ ${detail || (langRef.current === "ar" ? "تعذّر الاتصال بالمساعد الافتراضي — حاول مجدداً" : "Couldn't connect just now — please try again")}` });
    },
    onMessage: (m: { source: string; message: string }) => {
      if (!m.message || expiredConnectionRef.current) return;
      if (m.source === "user" && m.message.startsWith("[widget]")) return; // hidden widget → agent notes
      if (m.source === "user") { activityRef.current.lastUserAt = Date.now(); flushPendingCards(); }
      // Delivery markers steer the voice only; they never belong in the visible transcript.
      push({ kind: "msg", role: m.source === "user" ? "user" : "agent", text: m.source === "user" ? m.message : stripDeliveryTags(m.message) });
      if (m.source !== "user") flushPendingCards();
    },
  });

  statusRef.current = conversation.status;

  const expireSession = (expired: Session) => {
    ++authEpochRef.current;
    ++connectionAttemptRef.current;
    startingRef.current = false;
    expiredConnectionRef.current = true;
    switchingRef.current = true;
    commitSession(null);
    setSessionExpired(true);
    setCustomer(null); setProfile(null); setHistory([]); setProfileLoading(false); setAuthErr(null); setAuthOpen(false);
    commitOrder(null); setHoldLeft(null); warnedRef.current = {}; holdCheckRef.current = null;
    setItems([]); setInput(""); clearHumanMode(); setMode("idle"); setSseStatus("closed");
    pendingAckRef.current.clear(); completedActionsRef.current.clear(); cancelledOrdersRef.current.clear(); ackQueueRef.current = [];
    eventSeqRef.current.clear(); eventIdsRef.current.clear();
    if (ackTimerRef.current) clearTimeout(ackTimerRef.current);
    try {
      const saved = JSON.parse(localStorage.getItem("voxi.session") ?? "null");
      if (saved?.token === expired.token && saved.conversationId === expired.conversationId) localStorage.removeItem("voxi.session");
    } catch { /* Never remove a newer tab's session while recovering this one. */ }
    onAuth?.(null);
    // End only the transport. No logout, cancellation or fresh hold is sent to the backend.
    const ending = Promise.resolve().then(() => conversation.endSession()).catch(() => undefined).finally(() => {
      if (endingSessionRef.current === ending) { endingSessionRef.current = null; switchingRef.current = false; }
    });
    endingSessionRef.current = ending;
  };

  useEffect(() => {
    const onAccountSignal = (event: StorageEvent) => {
      if (event.key !== AUTH_CHANGE_SIGNAL) return;
      const current = sessionRef.current;
      const epoch = authEpochRef.current;
      if (!current || authBusyRef.current) return;
      void getState(current).then(() => {
        if (sessionRef.current?.token === current.token && epoch === authEpochRef.current && current.isLoggedIn) void refreshProfile(current);
      }).catch((error) => {
        if (isAuthorizationError(error) && !authBusyRef.current && sessionRef.current?.token === current.token && epoch === authEpochRef.current) expireSession(current);
      });
    };
    window.addEventListener("storage", onAccountSignal);
    return () => window.removeEventListener("storage", onAccountSignal);
  }, []);

  // link ElevenLabs conversation id ↔ concierge conversation
  useEffect(() => {
    if (conversation.status !== "connected" || authBusyRef.current || pendingLinkRef.current) return;
    const id = conversation.getId();
    if (!id || !session || session.conversationId === id) return;
    const current = sessionRef.current;
    if (!current) return;
    // No "reconnected" note: every connection starts with a fresh sessionContext snapshot (account,
    // active basket and its hold expiry), so asking the agent to look it up again only adds a round trip.
    const pending = linkConversation(current, id).then((linked) => {
      if (sessionRef.current?.token !== current.token || conversation.getId() !== id) return;
      commitSession(linked);
      if (linked.isLoggedIn) void refreshProfile(linked);
    }).catch(() => push({ kind: "note", text: langRef.current === "ar" ? "تعذر استعادة المحادثة. حاول إعادة الاتصال." : "Could not restore the conversation. Please reconnect." }))
      .finally(() => { if (pendingLinkRef.current === pending) pendingLinkRef.current = null; });
    pendingLinkRef.current = pending;
  }, [conversation.status, session?.conversationId, session?.token, authBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Cards that carry the order's hold expiry (Review & Pay, order summary) keep the timer in sync; a receipt ends it. */
  const trackOrderFromUi = (ui?: UiHint) => {
    if (!ui) return;
    if (isCancelledOrderUi(ui, cancelledOrdersRef.current)) return;
    if (ui.type === "login") { setAuthOpen(true); return; }
    if (ui.type === "qr") {
      if (receiptCompletesCurrentOrder(ui)) commitOrder(null);
      return;
    }
    // A new proposal replaces an expired hold: drop the stale "Seat hold expired" bar.
    const previousOrder = orderRef.current;
    if (ui.type === "booking_proposal" && previousOrder && !previousOrder.paid && previousOrder.expiresAtUtc && holdSeconds(previousOrder.expiresAtUtc) === 0) commitOrder(null);
    const m = ui.meta ?? {};
    if ((ui.type === "payment" || ui.type === "order") && m.userSessionId && m.expiresAtUtc)
      commitOrder((o) => ({ ...(o ?? {}), userSessionId: String(m.userSessionId), expiresAtUtc: String(m.expiresAtUtc), totalCents: Number(m.amountCents ?? o?.totalCents ?? 0) }));
  };

  // Same-device resume: a guest's reload within 30 minutes picks the booking back up; a later visit starts fresh.
  // A signed-in member's session is kept for up to 6 hours.
  useEffect(() => {
    if (!session) return;
    try {
      localStorage.setItem("voxi.session", JSON.stringify({ conversationId: session.conversationId, token: session.token, language: session.language, loggedIn: !!session.isLoggedIn, at: Date.now() }));
    } catch {}
  }, [session]);

  // Restore the signed-in session independently from the voice/text connection.
  useEffect(() => {
    let disposed = false;
    const epoch = authEpochRef.current;
    try {
      const saved = JSON.parse(localStorage.getItem("voxi.session") ?? "null") as { conversationId: string; token: string; at: number; language?: Lang; loggedIn?: boolean } | null;
      if (saved?.token && Date.now() - saved.at < sessionResumeMs(saved)) {
        restoreRef.current = createSession({ language: saved.language === "ar" ? "ar" : saved.language === "en" ? "en" : initialLang, modality: "text", conversationId: saved.conversationId, token: saved.token })
          .then((restored) => { if (!disposed && epoch === authEpochRef.current && !sessionRef.current) { commitSession(restored); setLang(restored.language); langRef.current = restored.language; void refreshProfile(restored); } })
          .catch(() => { if (!disposed && epoch === authEpochRef.current) try { localStorage.removeItem("voxi.session"); } catch {} });
      }
    } catch {}
    return () => { disposed = true; ++authEpochRef.current; ++connectionAttemptRef.current; };
  }, []);
  useEffect(() => { if (authOpen && session?.isLoggedIn) void refreshProfile(session); }, [authOpen]);

  // SSE subscription to concierge events
  useEffect(() => {
    if (!session || authBusy) return;
    let subscribed = true;
    let unsub = () => {};
    const onAuthInvalid = sessionExpiryHandler(session, authEpochRef.current,
      () => ({ session: sessionRef.current, epoch: authEpochRef.current, busy: authBusyRef.current || !subscribed }),
      () => { subscribed = false; unsub(); expireSession(session); });
    const epoch = authEpochRef.current;
    const isCurrent = () => subscribed && !authBusyRef.current && epoch === authEpochRef.current
      && sessionRef.current?.token === session.token && sessionRef.current.conversationId === session.conversationId;
    const onEvent = (e: WidgetEvent) => {
      if (!isCurrent()) return;
      if (!acceptWidgetEvent(eventSeqRef.current, eventIdsRef.current, session.conversationId, e)) return;
      switch (e.type) {
        case "ui.render":
          if (e.ui?.type) push({ kind: "cards", ui: e.ui }, { afterReply: e.origin === "agent" });
          trackOrderFromUi(e.ui);
          break;
        case "action.completed": {
          completedActionsRef.current.set(e.action.actionId, e.action);
          if (completedActionsRef.current.size > 100) completedActionsRef.current.delete(completedActionsRef.current.keys().next().value!);
          if (pendingAckRef.current.delete(e.action.actionId)) acknowledge(actionContext(e.action.type, { ok: e.action.status === "succeeded", action: e.action, error: e.action.error?.message }));
          const currentSession = sessionRef.current;
          const cancelled = cancelledCurrentOrder(e, orderRef.current?.userSessionId,
            { token: session.token, conversationId: session.conversationId, epoch },
            currentSession && !authBusyRef.current ? { token: currentSession.token, conversationId: currentSession.conversationId, epoch: authEpochRef.current } : null);
          if (cancelled) {
            cancelledOrdersRef.current.add(cancelled);
            if (cancelledOrdersRef.current.size > 100) cancelledOrdersRef.current.delete(cancelledOrdersRef.current.values().next().value!);
            // Clear the ref immediately so an in-flight expiry response also fails its hold snapshot guard.
            commitOrder(null); setHoldLeft(null); warnedRef.current = {}; holdCheckRef.current = null;
            setItems(xs => removeCancelledOrderCards(xs, cancelled));
          }
          if (e.ui?.type) push({ kind: "cards", ui: e.ui }, { afterReply: e.action.requestedBy === "agent" });
          else if (e.action.status !== "succeeded" && e.action.error?.message) push({ kind: "note", text: `⚠️ ${e.action.error.message}` });
          trackOrderFromUi(e.ui);
          break;
        }
        case "order.updated": {
          if (cancelledOrdersRef.current.has(e.userSessionId)) break;
          const sm = e.summary as Record<string, any>;
          if (sm?.expiresAtUtc !== orderRef.current?.expiresAtUtc) warnedRef.current = {};
          if (sm && (sm.state === "paid" || sm.state === "cancelled")) commitOrder(null);
          else commitOrder({ userSessionId: e.userSessionId, expiresAtUtc: sm?.expiresAtUtc, totalCents: sm?.totalCents, filmTitle: sm?.filmTitle, seats: sm?.seats });
          // An open Review & Pay sheet is never edited in place: the change (offer applied, points redeemed,
          // F&B added) produces a fresh sheet with the new totals and the earlier one stays as a record.
          if (sm && sm.state !== "paid" && sm.state !== "cancelled") {
            const sheet = [...itemsRef.current].reverse().find((it) => it.kind === "cards" && !it.archived && it.ui.type === "payment" && it.ui.meta?.userSessionId === e.userSessionId);
            if (sheet && sheet.kind === "cards")
              push({ kind: "cards", ui: { ...sheet.ui, items: [{ ...(sheet.ui.items?.[0] ?? {}), ...sm }], meta: { ...sheet.ui.meta, amountCents: sm.totalCents, expiresAtUtc: sm.expiresAtUtc ?? sheet.ui.meta?.expiresAtUtc, vat: { beforeVatCents: (sm.totalCents ?? 0) + (sm.loyaltyRedeemedCents ?? 0) - (sm.taxCents ?? 0), vatCents: sm.taxCents ?? 0, rate: 5 } } } }, { afterReply: true });
          }
          break;
        }
        case "transfer.status":
          // The handover card must not wait for an assistant reply that may never come (a person is speaking now).
          flushPendingCards();
          if (e.status === "ended") {
            endedTransfersRef.current.add(e.transferId);
            setHumanMode((current) => current?.transferId === e.transferId ? null : current);
            push({ kind: "note", text: t(lang, "transferEnded") });
          } else if (!endedTransfersRef.current.has(e.transferId)) setHumanMode({ transferId: e.transferId, agentName: e.agentName, status: e.status });
          break;
        case "human.message":
          if (endedTransfersRef.current.has(e.transferId)) break;
          setHumanMode((h) => ({ transferId: e.transferId, agentName: e.agentName ?? (h?.transferId === e.transferId ? h.agentName : undefined), status: "connected" }));
          flushPendingCards();
          push({ kind: "msg", role: "human", text: e.text, who: e.agentName });
          break;
        case "language.changed":
          langRef.current = e.language;
          setLang(e.language);
          if (sessionRef.current && sessionRef.current.language !== e.language)
            commitSession({ ...sessionRef.current, language: e.language, dynamicVariables: { ...sessionRef.current.dynamicVariables, language: e.language } });
          break;
        default:
          break;
      }
    };
    unsub = subscribe(session, onEvent, (status) => { if (isCurrent()) setSseStatus(status); }, onAuthInvalid);
    getState(session)
      .then((s) => {
        if (!isCurrent()) return;
        if (s.transfer && ["queued", "connected", "requested"].includes(s.transfer.status) && !endedTransfersRef.current.has(s.transfer.id)) setHumanMode({ transferId: s.transfer.id, agentName: s.transfer.agentName, status: s.transfer.status });
        if (s.conversation?.metadata?.activeOrder) void sendCommand(session, { type: "order.state" }).then((result) => {
          if (isCurrent() && result.ui) { trackOrderFromUi(result.ui); push({ kind: "cards", ui: result.ui }); }
        });
      })
      .catch((error) => { if (isAuthorizationError(error)) onAuthInvalid(); });
    return () => { subscribed = false; unsub(); };
  }, [session?.conversationId, session?.token, authBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  // mic level meter
  useEffect(() => {
    if (mode !== "voice") return;
    const iv = setInterval(() => { const volume = conversation.getInputVolume?.() ?? 0; setLevel(Math.min(1, volume * 3)); }, 100);
    return () => clearInterval(iv);
  }, [mode, conversation]);

  // keep the transcript pinned to the newest card: cards and images change height after they mount,
  // so scroll now, again shortly after, and whenever the content resizes while the user is near the bottom
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (!items.length) { el.scrollTop = 0; return; }
    if (!items.some((item) => item.kind !== "note")) return;
    const toBottom = () => el.scrollTo({ top: el.scrollHeight, behavior: "instant" as ScrollBehavior });
    toBottom();
    const t1 = setTimeout(toBottom, 150);
    const t2 = setTimeout(toBottom, 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [items]);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 240;
      if (nearBottom) el.scrollTo({ top: el.scrollHeight, behavior: "instant" as ScrollBehavior });
    });
    for (const child of Array.from(el.children)) ro.observe(child);
    const mo = new MutationObserver(() => {
      for (const child of Array.from(el.children)) ro.observe(child);
    });
    mo.observe(el, { childList: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);
  useEffect(() => onExpand?.(expanded), [expanded, onExpand]);
  // a location chosen before the conversation started is sent as soon as a session exists
  useEffect(() => {
    const l = locRef.current;
    if (session && l) void sendCommand(session, { type: "location", lat: l.lat, lng: l.lng, accuracyM: l.accuracyM, label: l.label, source: l.source });
  }, [session]);

  // The backend owns the hold expiry. Disconnecting never extends it.
  useEffect(() => {
    let disposed = false;
    const snapshot = (): HoldNoticeSnapshot | null => {
      const active = orderRef.current;
      const account = sessionRef.current;
      return !disposed && !authBusyRef.current && account && active?.expiresAtUtc
        ? { token: account.token, conversationId: account.conversationId, epoch: authEpochRef.current, userSessionId: active.userSessionId, expiresAtUtc: active.expiresAtUtc }
        : null;
    };
    const tick = () => {
      const current = orderRef.current;
      const left = holdSeconds(current?.expiresAtUtc);
      setHoldLeft(left);
      if (!current?.expiresAtUtc || left === null) return;
      const expiry = current.expiresAtUtc;
      const kind = left === 0 ? "expired" : "two";
      if (left > 120 || warnedRef.current[kind] === expiry) return;
      const expected = snapshot();
      const currentSession = sessionRef.current;
      if (!expected || !currentSession) return;
      const key = `${expected.token}:${expected.epoch}:${expected.userSessionId}:${expiry}:${kind}`;
      const previous = holdCheckRef.current;
      if (previous?.key === key && (previous.pending || previous.retryAt > Date.now())) return;
      const check = { key, pending: true, retryAt: 0 };
      holdCheckRef.current = check;
      void verifyHoldNotice(expected, snapshot, () => getState(currentSession),
        () => sendCommand(currentSession, { type: "order.state", userSessionId: expected.userSessionId }))
        .then((notice) => {
          if (!isCurrentHold(expected, snapshot())) return;
          if (notice.kind === "clear") { commitOrder(null); setHoldLeft(null); return; }
          if (notice.kind === "none") return;
          const marker = notice.kind === "expired" ? "expired" : "two";
          if (warnedRef.current[marker] === expiry) return;
          warnedRef.current[marker] = expiry;
          if (notice.kind === "expired") {
            push({ kind: "note", text: langRef.current === "ar" ? "انتهت مهلة المقاعد. احتفظنا باختياراتك؛ يمكنك طلب التحقق من المقاعد مجدداً." : "Your seat hold expired. Your choices are saved—check availability again when you’re ready." });
            if (notice.ui) push({ kind: "cards", ui: notice.ui });
            if (statusRef.current === "connected") conversation.sendContextualUpdate("The seat hold expired. Choices are preserved. Ask before recover_order confirmed:true; never automatically hold seats or restart the timer.");
          } else push({ kind: "note", text: langRef.current === "ar" ? "تبقى أقل من دقيقتين على مهلة المقاعد." : "Less than two minutes remain on your seat hold." });
        }).finally(() => { check.pending = false; check.retryAt = Date.now() + 10000; });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => { disposed = true; clearInterval(timer); };
  }, [order?.userSessionId, order?.expiresAtUtc, session?.token, lang]);

  // Guest "New chat": end the current conversation and start clean, with nothing carried over.
  const newChat = async () => {
    if (authBusyRef.current || sessionRef.current?.isLoggedIn) return;
    ++authEpochRef.current;
    ++connectionAttemptRef.current;
    startingRef.current = false;
    switchingRef.current = true;
    try { await conversation.endSession(); } catch { /* Nothing to end. */ } finally { switchingRef.current = false; }
    try { localStorage.removeItem("voxi.session"); } catch { /* Storage unavailable. */ }
    commitSession(null);
    setSessionExpired(false);
    commitOrder(null); setHoldLeft(null); warnedRef.current = {}; holdCheckRef.current = null;
    setItems([]); setInput(""); clearHumanMode(); setMode("idle"); setSseStatus("closed");
    pendingAckRef.current.clear(); completedActionsRef.current.clear(); cancelledOrdersRef.current.clear(); ackQueueRef.current = [];
    eventSeqRef.current.clear(); eventIdsRef.current.clear();
    if (ackTimerRef.current) clearTimeout(ackTimerRef.current);
  };

  const endChat = async () => {
    if (endingChat) return;
    setEndingChat(true);
    const current = sessionRef.current;
    const epoch = authEpochRef.current;
    try {
      if (humanMode && current) {
        const result = await sendCommand(current, { type: "human.end", transferId: humanMode.transferId });
        if (!result.ok) throw new Error("handover end failed");
        endedTransfersRef.current.add(humanMode.transferId);
        if (epoch !== authEpochRef.current || current.token !== sessionRef.current?.token) return;
        if (humanModeRef.current && humanModeRef.current.transferId !== humanMode.transferId) return;
        setHumanMode((active) => active?.transferId === humanMode.transferId ? null : active);
      }
      await conversation.endSession();
    } catch {
      if (epoch === authEpochRef.current) {
        idleEndedRef.current = false;
        activityRef.current.lastUserAt = Date.now();
        push({ kind: "note", text: langRef.current === "ar" ? "تعذر إنهاء المحادثة. حاول مجدداً." : "The conversation could not be ended. Please try again." });
      }
    } finally { setEndingChat(false); }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      // While seats are held and unpaid the guest may be paying (card entry, bank OTP in another tab):
      // the chat stays open until the hold ends, and the provider is told the guest is still here.
      const held = orderRef.current;
      const holdRunning = !!held && !held.paid && !!held.expiresAtUtc && Date.parse(held.expiresAtUtc) > Date.now();
      if (holdRunning) {
        if (statusRef.current === "connected" && Date.now() - activityRef.current.lastProviderPingAt >= 60000) {
          activityRef.current.lastProviderPingAt = Date.now();
          try { conversation.sendUserActivity(); } catch { /* best effort */ }
        }
        return;
      }
      const idleSince = Math.max(activityRef.current.lastUserAt, held?.expiresAtUtc && !held.paid ? Date.parse(held.expiresAtUtc) || 0 : 0);
      if ((statusRef.current === "connected" || humanMode) && !idleEndedRef.current && Date.now() - idleSince >= 180000) {
        idleEndedRef.current = true;
        void endChat();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [conversation.status, humanMode, endingChat]);

  const start = async (m: "voice" | "text") => {
    if (startingRef.current || authBusyRef.current || (statusRef.current === "connected" && !expiredConnectionRef.current)) return;
    startingRef.current = true;
    const attempt = ++connectionAttemptRef.current;
    const expected: ConnectionSnapshot = { attempt, epoch: authEpochRef.current };
    let startingSession: Session | null = null;
    const valid = () => isCurrentConnection(expected, {
      attempt: connectionAttemptRef.current, epoch: authEpochRef.current, busy: authBusyRef.current,
      token: sessionRef.current?.token, conversationId: sessionRef.current?.conversationId,
    });
    setMode(m);
    activityRef.current.lastUserAt = Date.now();
    idleEndedRef.current = false;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    try {
      await restoreRef.current;
      await endingSessionRef.current;
      if (!valid()) return;
      const s = sessionRef.current ?? (await createSession({ language: langRef.current, modality: m }));
      if (!valid()) return;
      commitSession(s);
      startingSession = s;
      expected.token = s.token; expected.conversationId = s.conversationId;
      setSessionExpired(false);
      const connection = await getSignedUrl(s, nextWelcomeVariant());
      if (!valid()) return;
      const { signedUrl, agentId, wsOrigin } = connection;
      const currentLang = langRef.current;
      const pendingOrder = orderRef.current;
      const dyn = connectionVariables(s, connection, currentLang, pendingOrder ? holdSeconds(pendingOrder.expiresAtUtc) === 0 ? "expired" : "active" : undefined);
      if (s.isLoggedIn && !customer) void refreshProfile(s);
      if (m === "voice") {
        const permission = await navigator.mediaDevices.getUserMedia({ audio: true });
        for (const track of permission.getTracks()) track.stop();
      }
      if (!valid()) return;
      // A text session always opens with the guest's own typed message, so the scripted welcome would land
      // after it and the model would greet a second time. Suppress it; the model's first reply greets once.
      const overrides = {
        agent: m === "text" ? { language: currentLang, firstMessage: "" } : { language: currentLang },
        conversation: { textOnly: m === "text" },
      } as const;
      const serverLocation = /eu\.residency/.test(wsOrigin ?? "") ? "eu-residency" : /in\.residency/.test(wsOrigin ?? "") ? "in-residency" : "us";
      const origin = { serverLocation, textOnly: m === "text" };
      expiredConnectionRef.current = false;
      const started = signedUrl
        ? conversation.startSession({ signedUrl, connectionType: "websocket", dynamicVariables: dyn, overrides, ...origin } as never)
        : conversation.startSession({ agentId, connectionType: "websocket", dynamicVariables: dyn, overrides, ...origin } as never);
      await Promise.race([
        started,
        new Promise((_, reject) => { watchdog = setTimeout(() => reject(new Error(currentLang === "ar" ? "تعذر الاتصال الآن. حاول مجدداً." : "The connection took too long. Please try again.")), 20000); }),
      ]);
      // Linking a successfully connected transport legitimately rotates its token and ID.
      // Authentication changes still invalidate it, independently of that link operation.
      if (attempt !== connectionAttemptRef.current || expected.epoch !== authEpochRef.current || authBusyRef.current) await conversation.endSession();
    } catch (error) {
      if (!valid()) return;
      if (isAuthorizationError(error) && startingSession) { expireSession(startingSession); return; }
      switchingRef.current = true;
      try { await conversation.endSession(); } catch { /* Clean up a partially opened connection. */ } finally { switchingRef.current = false; }
      if (m === "voice" && /NotAllowed|Permission|denied/i.test(String(error))) {
        push({ kind: "note", text: t(langRef.current, "micDenied") });
        startingRef.current = false;
        return await start("text");
      }
      push({ kind: "note", text: (error as Error).message || (langRef.current === "ar" ? "تعذر الاتصال. حاول مجدداً." : "Could not connect. Please try again.") });
      setMode("idle");
    } finally {
      if (watchdog) clearTimeout(watchdog);
      if (attempt === connectionAttemptRef.current) startingRef.current = false;
    }
  };

  /** Start a fresh voice/text transport while preserving the authenticated booking session. */
  const switchMode = async (m: "voice" | "text") => {
    if (switchingRef.current || startingRef.current || authBusyRef.current) return;
    switchingRef.current = true;
    try {
      if (statusRef.current === "connected") await conversation.endSession();
      // The SDK status prop can lag its resolved endSession promise by one render.
      statusRef.current = "disconnected";
      await start(m);
    } finally { switchingRef.current = false; }
  };

  const say = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      activityRef.current.lastUserAt = Date.now();
      if (humanMode && session) {
        push({ kind: "msg", role: "user", text });
        void sendCommand(session, { type: "human.message", transferId: humanMode.transferId, text });
        return;
      }
      if (statusRef.current === "connected") {
        activityRef.current.lastUserAt = Date.now();
        flushPendingCards();
        conversation.sendUserMessage(text);
        // Voice mode echoes the user's words through onMessage; text mode must render them itself.
        if (modeRef.current === "text") push({ kind: "msg", role: "user", text });
      } else push({ kind: "note", text: lang === "ar" ? "ابدأ المحادثة أولاً" : "Start the conversation first" });
    },
    [conversation, humanMode, session, lang, push, flushPendingCards],
  );

  const ask = async (text: string, proposalRef?: string) => {
    if (!text.trim()) return;
    if (humanMode && session) { say(text); return; }
    const epoch = authEpochRef.current;
    if (statusRef.current !== "connected") await start("text");
    // A card click is a new explicit request, so restore the transport before sending it.
    for (let i = 0; i < 66 && statusRef.current !== "connected" && epoch === authEpochRef.current; i++) await new Promise((resolve) => setTimeout(resolve, 300));
    if (epoch !== authEpochRef.current) return;
    if (statusRef.current === "connected") {
      if (proposalRef) acknowledge(JSON.stringify({ proposalEdit: { intent: "edit", baseProposalRef: proposalRef }, applied: false }));
      say(text);
    }
  };

  const act: CardActions = useMemo(
    () => ({
      say: (text, proposalRef) => { if (statusRef.current === "connected" || humanMode) {
        if (proposalRef && !humanMode) acknowledge(JSON.stringify({ proposalEdit: { intent: "edit", baseProposalRef: proposalRef }, applied: false }));
        say(text);
      } else void ask(text, proposalRef); },
      command: async (cmd) => {
        if (cmd.type === "proposal.preview") cmd = { ...cmd, input: { intent: "initial", ...(cmd.input as Record<string, unknown>) } };
        activityRef.current.lastUserAt = Date.now();
        const current = sessionRef.current;
        if (!current) return { ok: false, error: "no session" };
        const result = await sendCommand(current, { ...cmd, ...(["booking.select", "order.recover"].includes(String(cmd.type)) && !cmd.idempotencyKey ? { idempotencyKey: crypto.randomUUID() } : {}) })
          .catch((): Awaited<ReturnType<typeof sendCommand>> => ({ ok: false, error: langRef.current === "ar" ? "انقطع الاتصال. تحقق من حالة الحجز قبل إعادة المحاولة." : "The connection was interrupted. Check the booking status before trying again." }));
        if (sessionRef.current?.token !== current.token) return { ok: false, error: langRef.current === "ar" ? "تغيرت جلسة الحساب. تحقق من الطلب مجدداً." : "Your account session changed. Check the current booking again." };
        if (result.ui && isCancelledOrderUi(result.ui, cancelledOrdersRef.current)) return { ok: false, error: langRef.current === "ar" ? "تم إلغاء هذا الطلب." : "This order was cancelled." };
        if (result.ui) { trackOrderFromUi(result.ui); push({ kind: "cards", ui: result.ui }); }
        const mapFeedback = directSeatMapFeedback(String(cmd.type), result, langRef.current, statusRef.current === "connected", sessionRef.current?.token === current.token);
        if (mapFeedback) {
          push({ kind: "note", text: mapFeedback.text, polite: true });
          if (mapFeedback.context) acknowledge(mapFeedback.context);
        }
        const meaningful = ["seat.select", "payment.token", "booking.select", "proposal.preview", "proposal.accept", "refund.choose", "swap.refund.choose", "booking.link", "order.recover"].includes(String(cmd.type));
        if (meaningful && result.action && ["queued", "running"].includes(result.action.status)) {
          const completed = completedActionsRef.current.get(result.action.actionId);
          if (completed) acknowledge(actionContext(completed.type, { ok: completed.status === "succeeded", action: completed, error: completed.error?.message }));
          else pendingAckRef.current.add(result.action.actionId);
        }
        else if (meaningful) acknowledge(actionContext(String(cmd.type), result));
        if (!result.ok && !result.action && result.error) push({ kind: "note", text: result.error });
        return result;
      },
      selection: (selection) => {
        activityRef.current.lastUserAt = Date.now();
        acknowledge(JSON.stringify({ draftSelection: { kind: selection.kind, label: selection.label, cardLast4: selection.cardLast4 }, applied: false }));
      },
      decision: (decision) => {
        void (async () => {
          const epoch = authEpochRef.current;
          if (statusRef.current !== "connected") await start("text");
          for (let i = 0; i < 66 && statusRef.current !== "connected" && epoch === authEpochRef.current; i++) await new Promise((resolve) => setTimeout(resolve, 300));
          if (epoch !== authEpochRef.current || statusRef.current !== "connected") return;
          if (ackTimerRef.current) clearTimeout(ackTimerRef.current);
          const updates = ackQueueRef.current.splice(0);
          if (updates.length) conversation.sendContextualUpdate(updates.join("; "));
          conversation.sendContextualUpdate(JSON.stringify({ customerDecision: decision, instruction: "This decision applies only to the exact confirmationId on the selected card. Use the corresponding confirmation tool only if confirmed is true. Never substitute another pending confirmation or read internal IDs aloud." }));
          say(decision.confirmed ? (langRef.current === "ar" ? "نعم، أؤكد هذا الاختيار. تابع." : "Yes, I confirm this choice. Go ahead.") : (langRef.current === "ar" ? "لا، توقف من فضلك." : "No, please don't."));
        })();
      },
      openLink: (url) => window.open(url, "_blank", "noopener"),
      playTrailer: (id) => window.open(`https://www.youtube.com/watch?v=${id}`, "_blank", "noopener"),
      requestLogin: () => setAuthOpen(true),
      signedIn: !!customer,
      accountEmail: customer?.email,
    }),
    [say, ask, session, customer],
  );

  const connected = conversation.status === "connected";
  const dir = isRtl(lang) ? "rtl" : "ltr";
  // ---------- presentation-only state (launcher, unread badge, typing indicator, suggestions) ----------
  const [open, setOpen] = useState(initialOpen);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !event.composedPath().includes(menuRef.current as EventTarget)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", close, true);
    return () => { document.removeEventListener("pointerdown", close, true); document.removeEventListener("keydown", close, true); };
  }, [menuOpen]);
  const [unread, setUnread] = useState(0);
  const lastItem = items[items.length - 1];
  useEffect(() => {
    if (!open && lastItem && lastItem.kind !== "note") setUnread((n) => n + 1);
  }, [lastItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);
  const thinking = connected && !humanMode && lastItem?.kind === "msg" && lastItem.role === "user" && !conversation.isSpeaking;
  const suggestions = lang === "ar"
    ? ["اقترح لي فيلماً الليلة", "ماذا يُعرض بعد التاسعة قربي؟", "ساعدني في حجز سابق", "هل لبطاقتي عرض؟"]
    : ["Suggest a movie for tonight", "What's on after 9 PM near me?", "Help with an existing booking", "Does my card have an offer?"];
  const statusText = connected
    ? conversation.isSpeaking
      ? t(lang, "speaking")
      : mode === "voice"
        ? t(lang, "listening")
        : t(lang, "online")
    : conversation.status === "connecting"
      ? t(lang, "connecting")
      : "";
  const voiceState = !connected ? "off" : conversation.isSpeaking ? "speaking" : mode === "voice" ? "listening" : "idle";

  const handleUserActivity = () => recordUserActivity(activityRef.current, connected ? () => conversation.sendUserActivity() : undefined);

  if (!open)
    return (
      <button className="cinema-launcher" dir={dir} onClick={() => setOpen(true)} aria-label={t(lang, "askVoxi")}>
        <BrandMark size={32} state={voiceState} />
        <span className="launcher-copy">{t(lang, "askVoxi")}</span>
        {unread ? <span className="unread">{unread}</span> : null}
      </button>
    );

  return (
    <div className={`widget ${expanded ? "expanded" : ""} state-${voiceState}`} dir={dir} onPointerDownCapture={handleUserActivity} onKeyDownCapture={handleUserActivity} onWheelCapture={handleUserActivity} onInputCapture={handleUserActivity}>
      <div className="widget-head v3">
        <div className="titles">
          <b><BrandLogo height={28} /><span className="sr-only">{t(lang, "title")}</span></b>
        </div>
        <div className="va">
          <b>{lang === "ar" ? "المساعد الافتراضي" : "Virtual Assistant"}</b>
          <small>
            <span className={`dot ${statusText ? voiceState : "ready"}`} />
            {humanMode ? `${t(lang, "human")}${humanMode.agentName ? ` · ${humanMode.agentName}` : ""}` : statusText || (lang === "ar" ? "متصل" : "Online")}
          </small>
        </div>
        <button type="button" className="langbtn" onClick={() => void changeLanguage(lang === "ar" ? "en" : "ar", true)} aria-label={lang === "ar" ? "English" : "العربية"} title={`events: ${sseStatus}`}>
          {lang === "ar" ? "EN" : "ع"}
        </button>
        <button type="button" className={`iconbtn account ${customer ? "on" : ""}`} onClick={() => setAuthOpen(true)} aria-label={customer ? (lang === "ar" ? "حسابك" : "Your account") : lang === "ar" ? "تسجيل الدخول" : "Sign in"} title={customer ? `${customer.firstName}` : lang === "ar" ? "تسجيل الدخول" : "Sign in"}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" /></svg>
          {customer ? <span className="sr-only">{lang === "ar" ? "تم الدخول" : "Signed in"}</span> : null}
        </button>
        <div className="headmenu" ref={menuRef}>
          <button type="button" className="iconbtn" aria-haspopup="menu" aria-expanded={menuOpen} aria-label={lang === "ar" ? "المزيد" : "More"} onClick={() => setMenuOpen((x) => !x)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
          </button>
          {menuOpen ? (
            <div className="headmenu-list" role="menu" onClick={() => setMenuOpen(false)}>
              <button type="button" role="menuitem" onClick={() => setExpanded((x) => !x)}>{expanded ? (lang === "ar" ? "تصغير النافذة" : "Smaller window") : lang === "ar" ? "توسيع النافذة" : "Bigger window"}</button>
              {customer ? <button type="button" role="menuitem" onClick={() => void doLogout()}>{lang === "ar" ? "تسجيل الخروج" : "Sign out"}</button> : <button type="button" role="menuitem" onClick={() => setAuthOpen(true)}>{lang === "ar" ? "تسجيل الدخول" : "Sign in"}</button>}
              {!customer && !humanMode && items.some((item) => item.kind !== "note") ? <button type="button" role="menuitem" onClick={() => void newChat()}>{lang === "ar" ? "محادثة جديدة" : "New chat"}</button> : null}
              {connected || humanMode ? <button type="button" role="menuitem" className="danger" disabled={endingChat} onClick={() => void endChat()}>{t(lang, "end")}</button> : null}
            </div>
          ) : null}
        </div>
        <button className="iconbtn" onClick={() => setOpen(false)} title={t(lang, "minimise")} aria-label={t(lang, "minimise")}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14" /></svg>
        </button>
      </div>

      <LocationBar lang={lang} loc={loc} onChange={changeLoc} />

      <div className="widget-body" ref={bodyRef}>
        {sessionExpired ? <div className="sysnote" role="alert">
          <p>{lang === "ar" ? "انتهت صلاحية الجلسة أو تغيرت. أعد الاتصال أو سجّل الدخول مجدداً. لم نلغِ حجوزاتك." : "Your session expired or changed. Reconnect or sign in again. Your bookings have not been cancelled."}</p>
          <div className="startbtns">
            <button className="btn primary" onClick={() => void start("text")}>{lang === "ar" ? "إعادة الاتصال" : "Reconnect"}</button>
            <button className="btn ghost" onClick={() => setAuthOpen(true)}>{lang === "ar" ? "تسجيل الدخول" : "Sign in"}</button>
          </div>
        </div> : null}
        {mode !== "idle" && !items.length && !connected ? (
          <div className="start">
            <BrandMark size={50} state="listening" />
            <p>{t(lang, "connecting")}</p>
          </div>
        ) : null}
        {!sessionExpired && mode === "idle" && !connected && items.every((i) => i.kind === "note") ? (
          <div className="start v3">
            <BrandMark size={50} />
            <span className="eyebrow">{greeting(lang)}</span>
            <h3>{lang === "ar" ? "فيلمك القادم يبدأ هنا." : "Your next great movie starts here."}</h3>
            <p>{lang === "ar" ? "اسأل بصوتك أو اكتب — أجد لك فيلماً وأحجز المقاعد وأضيف الوجبات." : "Ask by voice or type — I'll find a film, hold the seats and add snacks."}</p>
            <button className="btn primary talk" onClick={() => start("voice")}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
                {lang === "ar" ? "اضغط وتحدّث" : "Tap to talk"}
              </span>
              <span className="wave" aria-hidden="true"><i /><i /><i /><i /><i /></span>
            </button>
            <p className="tryhead">{lang === "ar" ? "جرّب أن تسأل" : "Try asking"}</p>
            {suggestions.map((s, i) => (
              <button key={s} type="button" className="tryrow" onClick={() => ask(s)}>
                <Icon name={(["star", "clock", "link", "card"] as const)[i] ?? "star"} size={17} />
                <span>{s}</span>
                <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 6l6 6-6 6" /></svg>
              </button>
            ))}
          </div>
        ) : null}
        {items.map((it, idx) => {
          const prev = items[idx - 1];
          const first = !(prev && prev.kind === "msg" && it.kind === "msg" && prev.role === it.role);
          return it.kind === "msg" ? (
            <div key={it.id} className={`msgrow ${it.role} ${first ? "first" : ""}`}>
              {it.role === "agent" ? <BrandMark size={30} state={idx === items.length - 1 ? voiceState : undefined} /> : it.role !== "user" ? <span className={`avatar ${it.role}`}>{it.who?.[0] ?? "☺"}</span> : null}
              <div className={`msg ${it.role}`}>
                {it.role !== "user" && first ? <span className="who">{it.role === "human" ? (it.who ?? t(lang, "human")) : t(lang, "agent")}</span> : null}
                {it.text}
              </div>
            </div>
          ) : it.kind === "cards" ? (
            <div key={it.id}>{it.archived ? <FrozenCards label={lang === "ar" ? "خطوة سابقة" : "Earlier step"}><Cards ui={it.ui} lang={lang} act={act} frozen /></FrozenCards> : <Cards ui={it.ui} lang={lang} act={act} />}</div>
          ) : it.kind === "feedback" ? (
            <div key={it.id} className="cards">
              <Feedback lang={lang} act={act} />
            </div>
          ) : (
            <div key={it.id} className="sysnote" role={it.polite ? "status" : undefined} aria-live={it.polite ? "polite" : undefined} aria-atomic={it.polite ? true : undefined}>
              {it.text}
            </div>
          );
        })}
        {thinking && mode === "text" ? (
          <div className="msgrow agent first">
            <span className="avatar agent" />
            <div className="msg agent typing" aria-label={t(lang, "thinking")}><i /><i /><i /></div>
          </div>
        ) : null}
        {humanMode && humanMode.status !== "ended" ? <div className="transfer">{humanMode.status === "connected" ? `${t(lang, "connectedTo")} ${humanMode.agentName ?? t(lang, "human")}` : t(lang, "transferring")}</div> : null}
      </div>

      {dev ? (
        <div className="widget-foot" style={{ background: "#fff8e1", flexWrap: "wrap" }} data-testid="devpanel">
          <input style={{ flex: "0 0 160px" }} value={devName} onChange={(e) => setDevName(e.target.value)} placeholder="tool name" />
          <input style={{ flex: 1 }} value={devInput} onChange={(e) => setDevInput(e.target.value)} placeholder='{"query":"…"}' />
          <button
            className="btn"
            onClick={async () => {
              const s = session ?? (await createSession({ language: lang, modality: "text" }));
              commitSession(s);
              if (mode === "idle") setMode("text");
              push({ kind: "msg", role: "user", text: `[dev] ${devName} ${devInput}` });
              const r = await devTool(s, devName, JSON.parse(devInput || "{}"));
              push({ kind: "msg", role: "agent", text: r.speech ?? r.error?.message ?? JSON.stringify(r).slice(0, 300) });
            }}
          >
            Run
          </button>
        </div>
      ) : null}

      {mode === "voice" && connected ? (
        <div className={`voicestage ${voiceState}`}>
          <div className="wave" aria-hidden="true">
            {Array.from({ length: 9 }, (_, i) => (
              <i key={i} style={{ height: `${Math.max(14, Math.round((conversation.isSpeaking ? 40 + 30 * Math.abs(Math.sin((Date.now() / 160 + i) % Math.PI)) : 14 + level * 70 * (1 - Math.abs(i - 4) / 5)) ))}%` }} />
            ))}
          </div>
          <span className="vs-text">{conversation.isSpeaking ? t(lang, "speaking") : muted ? (lang === "ar" ? "الميكروفون مغلق" : "Mic off") : t(lang, "listening")}</span>
          <button
            className={`btn ghost small mute ${muted ? "on" : ""}`}
            onClick={() => {
              const next = !muted;
              setMuted(next);
            }}
            title={muted ? (lang === "ar" ? "تشغيل الميكروفون" : "Turn microphone on") : lang === "ar" ? "كتم الميكروفون فقط" : "Mute microphone only"}
            aria-pressed={muted}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" />{muted ? <path d="m3 3 18 18" /> : null}</svg>
            {muted ? (lang === "ar" ? "الميكروفون مغلق" : "Mic off") : lang === "ar" ? "الميكروفون يعمل" : "Mic on"}
          </button>
        </div>
      ) : null}
      {holdLeft != null && order && !order.paid ? (
        <div className={`holdbar ${holdLeft <= 45 ? "urgent" : holdLeft <= 120 ? "warn" : ""}`} role="status">
          <span>{holdLeft === 0 ? (lang === "ar" ? "انتهت مهلة المقاعد" : "Seat hold expired") : (lang === "ar" ? "المقاعد محجوزة" : "Seats held")}{order.seats ? ` · ${seatRange(order.seats)}` : ""}</span>
          <b>{`${Math.floor(holdLeft / 60)}:${String(holdLeft % 60).padStart(2, "0")}`}</b>
          {holdLeft === 0 ? <button className="btn small" onClick={() => void act.command({ type: "order.recover", userSessionId: order.userSessionId, confirmed: true })}>{lang === "ar" ? "تحقق واحجز المقاعد مجدداً" : "Check and hold seats again"}</button> : null}
        </div>
      ) : null}

      {!connected && conversation.status !== "connecting" && items.some((item) => item.kind !== "note") ? <div className="reconnect-bar"><button className="btn primary" onClick={() => void start("text")}>{lang === "ar" ? "متابعة المحادثة" : "Reconnect to chat"}</button>{order ? <span>{lang === "ar" ? "اختياراتك محفوظة" : "Your choices are saved"}</span> : null}</div> : null}
      <div className="widget-foot">
        {!humanMode ? (
          <button
            className={`iconbtn mic ${mode === "voice" ? "on" : ""}`}
            title={mode === "voice" ? t(lang, "switchToText") : t(lang, "switchToVoice")}
            aria-label={mode === "voice" ? t(lang, "switchToText") : t(lang, "switchToVoice")}
            onClick={() => switchMode(mode === "voice" ? "text" : "voice")}
          >
            {mode === "voice" ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 0 1-8 8H8l-5 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
            )}
          </button>
        ) : null}
        <div className="composer">
          <input value={input} placeholder={mode === "idle" && !humanMode ? (lang === "ar" ? "…أو اكتب سؤالك" : "…or type your question") : lang === "ar" ? "اسأل عن الأفلام أو المواعيد أو حجزك" : "Ask about movies, times or your booking"} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { if (connected || humanMode) say(input); else void ask(input); setInput(""); } }} onFocus={handleUserActivity} />
          <button className="sendbtn" disabled={!input.trim()} onClick={() => { if (connected || humanMode) say(input); else void ask(input); setInput(""); }} aria-label={t(lang, "send")} title={t(lang, "send")}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Welcome eyebrow in cinema-local time. */
function greeting(lang: Lang) {
  const h = Number(new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: "Asia/Dubai" }).format(new Date()));
  if (lang === "ar") return h < 12 ? "صباح الخير" : "مساء الخير";
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}
